import React, { useState, useEffect } from 'react';
import {
    Loader2,
    CheckCircle2,
    AlertCircle,
    ArrowLeft,
    MessageSquare,
    FileText,
    ExternalLink,
    RefreshCw
} from 'lucide-react';
import { moduleService } from '../../../services/modules';

interface ModuleDetailProps {
    moduleId: string;
    executionId: string;
    onBack: () => void;
}

export const ModuleDetailView = ({ moduleId, executionId, onBack }: ModuleDetailProps) => {
    const [status, setStatus] = useState<{
        executionId: string;
        status: string;
        currentStage: string;
        progress: number;
        logs: string[];
        artifacts: any;
    } | null>(null);
    const [polling, setPolling] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const pollStatus = async () => {
        try {
            const res = await fetch(`/api/modules/status/${executionId}`);
            const data = await res.json();
            if (data.success) {
                setStatus(data);
                if (data.status === 'completed' || data.status === 'failed') {
                    setPolling(false);
                }
            } else {
                setError(data.error);
            }
        } catch (err: any) {
            setError(err.message);
        }
    };

    useEffect(() => {
        if (polling) {
            pollStatus();
            const interval = setInterval(pollStatus, 3000);
            return () => clearInterval(interval);
        }
    }, [polling]);

    if (error) {return (
        <div className="p-8 text-center">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-slate-800 mb-2">Execution Error</h3>
            <p className="text-slate-500 mb-6">{error}</p>
            <button onClick={onBack} className="px-6 py-2 bg-slate-900 text-white rounded-full font-bold">Go Back</button>
        </div>
    );}

    if (!status) {return (
        <div className="p-8 text-center">
            <Loader2 className="mx-auto text-blue-500 animate-spin mb-4" size={48} />
            <p className="text-slate-500 animate-pulse">Initializing execution flow...</p>
        </div>
    );}

    const isCompleted = status.status === 'completed';
    const isFailed = status.status === 'failed';

    return (
        <div className="max-w-4xl mx-auto space-y-8 p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-slate-400 hover:text-slate-800 transition-colors font-bold text-xs uppercase tracking-widest"
            >
                <ArrowLeft size={16} /> Back to Hub
            </button>

            <div className="flex items-center justify-between bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                <div>
                    <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tight">
                        {moduleId.replace('_', ' ')}
                    </h2>
                    <div className="flex items-center gap-2 mt-2">
                        <span className={`w-2 h-2 rounded-full ${isCompleted ? 'bg-emerald-500' : isFailed ? 'bg-red-500' : 'bg-blue-500 animate-pulse'}`} />
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                            {status.status} • {status.currentStage}
                        </span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-4xl font-black text-slate-800">{status.progress}%</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Completion</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                        <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-6 flex items-center gap-2">
                            <FileText size={16} /> Execution Log
                        </h3>
                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-4">
                            {status.logs.map((log, i) => (
                                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100 text-sm text-slate-600 leading-relaxed">
                                    <span className="text-slate-300 font-mono text-[10px] mt-1">{i + 1}</span>
                                    {log}
                                </div>
                            ))}
                            {polling && (
                                <div className="flex gap-4 p-4 rounded-2xl bg-blue-50 border border-blue-100 text-sm text-blue-600 animate-pulse">
                                    <Loader2 size={16} className="animate-spin mt-1" />
                                    Processing current stage...
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                        <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-6 flex items-center gap-2">
                            <ExternalLink size={16} /> Artifacts
                        </h3>
                        <div className="space-y-3">
                            {Object.entries(status.artifacts).length === 0 ? (
                                <p className="text-xs text-slate-400 italic">No artifacts produced yet.</p>
                            ) : (
                                Object.entries(status.artifacts).map(([stage, data]: [string, any]) => (
                                    <div key={stage} className="p-3 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between group cursor-pointer hover:bg-white transition-all">
                                        <span className="text-xs font-bold text-slate-600 truncate uppercase">{stage}</span>
                                        <ExternalLink size={14} className="text-slate-300 group-hover:text-blue-600" />
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-xl">
                        <h3 className="text-sm font-black uppercase tracking-widest mb-4 flex items-center gap-2">
                            <MessageSquare size={16} /> Agent Insights
                        </h3>
                        <p className="text-xs text-slate-400 leading-relaxed mb-6">
                            The AI orchestrator is currently managing the transition between stages. Next expected update:
                            <span className="text-white font-bold"> {status.currentStage} completion</span>.
                        </p>
                        <button
                            onClick={pollStatus}
                            disabled={!polling}
                            className="w-full py-3 bg-white text-slate-900 rounded-full text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={polling ? 'animate-spin' : ''} />
                            Force Refresh
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
